/* Audit Journal — iter78. Unified log of SLA alerts, calendar changes and
 * payslip recomputes, with filters and XLSX export (RGPD-ready).
 * Route: /audit-journal (admin/director/rh). */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DatePicker } from '../components/ui/date-picker';
import { ClipboardText, FileXls, ArrowClockwise } from '@phosphor-icons/react';
import api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  sla_alert: { label: 'Alerte SLA', color: '#B91C1C', bg: '#FEE2E2', icon: '🔴' },
  calendar_change: { label: 'Modif calendrier', color: '#0052FF', bg: '#EEF2FF', icon: '📅' },
  payslip_recompute: { label: 'Régénération paie', color: '#059669', bg: '#ECFDF5', icon: '💰' },
};

export default function AuditJournal() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kind, setKind] = useState('all');
  const [className, setClassName] = useState('');
  const [week, setWeek] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (kind !== 'all') params.kind = kind;
      if (className) params.class_name = className;
      if (week) params.week = week;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get('/audit-journal', { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); }
  }, [kind, className, week, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const xlsxHref = () => {
    const qs = new URLSearchParams();
    if (kind !== 'all') qs.set('kind', kind);
    if (className) qs.set('class_name', className);
    if (week) qs.set('week', week);
    if (dateFrom) qs.set('date_from', dateFrom);
    if (dateTo) qs.set('date_to', dateTo);
    const s = qs.toString();
    return `${API_URL}/api/audit-journal.xlsx${s ? '?' + s : ''}`;
  };

  return (
    <div className="p-6 space-y-4" data-testid="audit-journal-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-[#0F172A] flex items-center gap-2">
            <ClipboardText size={28} weight="duotone" className="text-[#0052FF]" />
            Journal d'audit — SLA, calendrier & paie
          </h1>
          <p className="text-sm text-[#64748B]">
            Vue unifiée pour les audits RGPD : alertes SLA, changements de
            calendrier et régénérations de payslips. Filtrable et exportable en XLSX.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="aj-refresh">
            <ArrowClockwise size={14} className="mr-1" />
            {loading ? '…' : 'Actualiser'}
          </Button>
          <a
            href={xlsxHref()}
            className="inline-flex items-center h-9 px-3 rounded-md border border-emerald-400 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold"
            data-testid="aj-xlsx-btn"
          >
            <FileXls size={14} className="mr-1" /> Export XLSX
          </a>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-[#64748B]">Type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="h-9" data-testid="aj-filter-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="sla_alert">Alertes SLA</SelectItem>
                <SelectItem value="calendar_change">Modifs calendrier</SelectItem>
                <SelectItem value="payslip_recompute">Régénérations paie</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-[#64748B]">Classe</Label>
            <Input value={className} onChange={(e) => setClassName(e.target.value)}
              placeholder="Ex: CE2-A" className="h-9"
              data-testid="aj-filter-class" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-[#64748B]">Semaine ISO</Label>
            <Input value={week} onChange={(e) => setWeek(e.target.value)}
              placeholder="2026-W07" className="h-9"
              data-testid="aj-filter-week" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-[#64748B]">Du</Label>
            <DatePicker value={dateFrom} onChange={setDateFrom}
              testid="aj-filter-from" className="w-full" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-[#64748B]">Au</Label>
            <DatePicker value={dateTo} onChange={setDateTo}
              testid="aj-filter-to" className="w-full" />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs text-[#64748B] mb-2">
            {items.length} entrée{items.length > 1 ? 's' : ''}
          </p>
          {loading ? (
            <p className="text-center py-8 text-[#94A3B8]">Chargement…</p>
          ) : items.length === 0 ? (
            <p className="text-center py-8 text-[#94A3B8]">
              Aucune entrée pour ces filtres.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="aj-table">
                <thead>
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Date</th>
                    <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Type</th>
                    <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Ressource</th>
                    <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Résumé</th>
                    <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((e, i) => {
                    const meta = KIND_META[e.kind] || {
                      label: e.kind, color: '#64748B', bg: '#F1F5F9', icon: '•',
                    };
                    const canRetry = e.kind === 'payslip_recompute'
                      && (e.failed || 0) > 0 && e.run_id;
                    return (
                      <tr key={i} className="border-b border-[#F1F5F9]"
                        data-testid={`aj-row-${i}`}>
                        <td className="py-2 px-2 text-xs text-[#64748B] font-mono">
                          {e.when ? new Date(e.when).toLocaleString('fr-FR') : '—'}
                        </td>
                        <td className="py-2 px-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
                            style={{ background: meta.bg, color: meta.color }}>
                            {meta.icon} {meta.label}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-xs">
                          {e.class_name || '—'}
                        </td>
                        <td className="py-2 px-2 text-xs text-[#0F172A]">
                          {e.summary}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {canRetry ? (
                            <Button
                              type="button" size="sm" variant="outline"
                              className="h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-50"
                              data-testid={`aj-retry-${e.run_id}`}
                              onClick={async () => {
                                try {
                                  const { data } = await api.post(
                                    `/school-calendar/recompute-retry/${e.run_id}`,
                                  );
                                  toast.success(
                                    `Retry : ${data.recomputed} OK, ${data.failed} échec(s) restant(s)`,
                                  );
                                  load();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Erreur retry');
                                }
                              }}
                            >
                              ↻ Réessayer ({e.failed})
                            </Button>
                          ) : (
                            <span className="text-[#CBD5E1] text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
